import React from 'react';
import { ShieldCheckIcon, UserPlusIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { usePortalAuth } from '../../context/PortalAuth';
const ORG_USERS = [{
  name: 'Ama Mensah',
  email: 'a.mensah@northbridge.org',
  role: 'Client admin'
}, {
  name: 'Kofi Adjei',
  email: 'k.adjei@northbridge.org',
  role: 'Client user'
}, {
  name: 'Grace Otieno',
  email: 'g.otieno@northbridge.org',
  role: 'Client user'
}];
const NOTIFICATIONS = [{
  id: 'deliverable',
  label: 'A deliverable is uploaded'
}, {
  id: 'status',
  label: 'A project status changes'
}, {
  id: 'invoice',
  label: 'An invoice is raised'
}];
export function PortalAccount() {
  const {
    user
  } = usePortalAuth();
  const [prefs, setPrefs] = React.useState<Record<string, boolean>>({
    deliverable: true,
    status: true,
    invoice: true
  });
  const [saved, setSaved] = React.useState(false);
  if (!user) return null;
  const isAdmin = user.role === 'client-admin' || user.role === 'super-admin';
  return <div className="space-y-8" data-id="element-1158" data-mp="a930gokqoouj">
      <header data-id="element-1159" data-mp="a8b59ev20t4s">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-1160" data-mp="a9vz129jo81l">Account</p>
        <h1 className="mt-3 font-display text-[30px] leading-tight text-ink sm:text-[36px]" data-id="element-1161" data-mp="f74m9h9jv29p">Your settings</h1>
      </header>

      <section aria-labelledby="profile" className="rounded-md border border-line bg-white p-6" data-id="element-1162" data-mp="jb3yhnnsrex9">
        <h2 id="profile" className="font-display text-[20px] text-ink" data-id="element-1163" data-mp="o8lxal83odrj">
          Profile
        </h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1164" data-mp="t644a62k4iww">
          <Field id="acct-name" label="Full name" required data-id="element-1165" data-mp="xyf7bhslgkto">
            <input id="acct-name" type="text" defaultValue={user.name} className={inputClass} data-id="element-1166" data-mp="y11gflv4v8e0" />
          </Field>
          <Field id="acct-email" label="Email address" required data-id="element-1167" data-mp="16zn3nubjvbl4">
            <input id="acct-email" type="email" defaultValue={user.email} className={inputClass} data-id="element-1168" data-mp="16xad4srq7swg" />
          </Field>
        </div>
        <p className="mt-4 text-small text-muted" data-id="element-1169" data-mp="381bileswjbpm">
          Organisation: {user.organisationName}. Role: {user.role.replace('-', ' ')}.
        </p>
      </section>

      <section aria-labelledby="security" className="rounded-md border border-line bg-white p-6" data-id="element-1170" data-mp="341ub6ftx04ht">
        <h2 id="security" className="font-display text-[20px] text-ink" data-id="element-1171" data-mp="3hspd185zxlbw">
          Password and security
        </h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1172" data-mp="3cyutq5uaz8dc">
          <Field id="acct-current" label="Current password" required data-id="element-1173" data-mp="3ro94nidj1xuu">
            <input id="acct-current" type="password" autoComplete="current-password" className={inputClass} data-id="element-1174" data-mp="3mr0mviwajvu4" />
          </Field>
          <Field id="acct-new" label="New password" required hint="At least twelve characters." data-id="element-1175" data-mp="4jkzfjwnn7g4">
            <input id="acct-new" type="password" autoComplete="new-password" className={inputClass} data-id="element-1176" data-mp="3vsgy1suxk1ze" />
          </Field>
        </div>
        <Button variant="secondary" className="mt-5" data-id="element-1177" data-mp="26ebumiu0hy39">
          Update password
        </Button>

        <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6" data-id="element-1178" data-mp="21f903sbnra9j">
          <div className="flex items-start gap-3" data-id="element-1179" data-mp="gxga2nhj3ibj">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-gold" aria-hidden="true" data-id="element-1180" data-mp="lt5byxh0tbb0" />
            <div data-id="element-1181" data-mp="83lcq1mnew6g">
              <p className="text-[15px] font-medium text-ink" data-id="element-1182" data-mp="820kwjrpxrca">Multi factor authentication</p>
              <p className="mt-1 text-[14px] text-body" data-id="element-1183" data-mp="c3c34489jaag">
                {user.mfaEnabled ? 'Enabled through an authenticator app. Required for your role.' : 'Not enabled. We recommend turning this on.'}
              </p>
            </div>
          </div>
          <Button variant="secondary" size="sm" data-id="element-1184" data-mp="3laddts903rwk">
            {user.mfaEnabled ? 'Manage' : 'Enable'}
          </Button>
        </div>
      </section>

      <section aria-labelledby="notifications" className="rounded-md border border-line bg-white p-6" data-id="element-1185" data-mp="3pcqisefxblsa">
        <h2 id="notifications" className="font-display text-[20px] text-ink" data-id="element-1186" data-mp="1jg4dblinr7f8">
          Email me when
        </h2>
        <ul className="mt-5 space-y-4" data-id="element-1187" data-mp="ysmm31xd5trz">
          {NOTIFICATIONS.map(item => <li key={item.id} className="flex items-center gap-3" data-id="element-1188" data-mp="yqjx8l7y66ou">
              <input id={`notif-${item.id}`} type="checkbox" checked={prefs[item.id]} onChange={event => {
            setPrefs(current => ({
              ...current,
              [item.id]: event.target.checked
            }));
            setSaved(false);
          }} className="h-4 w-4 rounded-[2px] border-line accent-[#FF5C1A]" data-id="element-1189" data-mp="tucokko62h37" />
              <label htmlFor={`notif-${item.id}`} className="text-[15px] text-body" data-id="element-1190" data-mp="b20mlz1fdhtl">
                {item.label}
              </label>
            </li>)}
        </ul>
        <Button variant="secondary" className="mt-6" onClick={() => setSaved(true)} data-id="element-1191" data-mp="16gttli5qz92">
          Save preferences
        </Button>
        {saved && <p role="status" className="mt-3 text-small text-muted" data-id="element-1192" data-mp="3o9tyru4sn01f">
            Your preferences have been saved.
          </p>}
      </section>

      {isAdmin && <section aria-labelledby="users" className="rounded-md border border-line bg-white p-6" data-id="element-1193" data-mp="23tvycgwvrodu">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1194" data-mp="27vhhul3x70x0">
            <h2 id="users" className="font-display text-[20px] text-ink" data-id="element-1195" data-mp="2ct0vpjwphbof">
              Users at {user.organisationName}
            </h2>
            <Button variant="secondary" size="sm" data-id="element-1196" data-mp="1k3txyn52sq48">
              <UserPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1197" data-mp="1k5506iivhpt8" />
              Invite a user
            </Button>
          </div>
          <ul className="mt-5 divide-y divide-line" data-id="element-1198" data-mp="1p0u6r5tb86z1">
            {ORG_USERS.map(member => <li key={member.email} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-id="element-1199" data-mp="1tzx03szxgor6">
                <div data-id="element-1200" data-mp="1yujd20lnqpdd">
                  <p className="text-[15px] text-ink" data-id="element-1201" data-mp="11dlwsakxs9qo">{member.name}</p>
                  <p className="mt-0.5 text-small text-muted" data-id="element-1202" data-mp="11b83o79th7lr">{member.email}</p>
                </div>
                <span className="text-[14px] text-body" data-id="element-1203" data-mp="16a2n9bflz06x">{member.role}</span>
              </li>)}
          </ul>
        </section>}
    </div>;
}